import { SurveysServiceInstance } from '@/services/SurveysService'
import { formErrorsHandler } from '@/utils/formErrorsHandler'

// Cuando hay que volver a pedir la lista a raíz de un evento que les llega a
// todos a la vez (reconexión masiva, una encuesta nueva), cada navegador espera
// un tiempo al azar de hasta esto, para no llegar todos en el mismo segundo.
const REFETCH_JITTER_MS = 8000

// Esperas entre reintentos al enviar una respuesta (más un poco al azar).
const RETRY_DELAYS_MS = [2000, 5000, 10000]

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

// `reason`: 'closed' (el admin la cerró), 'invalid' (con `fields`, los errores
// por pregunta) o 'unavailable' (no se pudo tras los reintentos).
export class SurveySubmitError extends Error {
  constructor(reason, fields = {}) {
    super(reason)
    this.reason = reason
    this.fields = fields
  }
}

// Encuestas de satisfacción del lado del asistente.
//
// `items` son todas las encuestas del evento, con las preguntas, cargadas una
// vez al entrar (GET event/surveys): solo las visibles. Activar, cerrar u
// ocultar llega como SurveysChanged y solo cambia el estado acá: no se pide
// nada al backend (salvo que se muestre una que no está).
// `active` es la que está abierta en el modal.
const initState = () => ({
  items: [],
  active: null,
  // Lo que marcó en una encuesta que cerró sin enviar, por uuid.
  drafts: {},
})

const getters = {
  // Activas sin responder: el número del ícono.
  available: (state) =>
    state.items.filter((survey) => survey.status === 'open' && !survey.answered),
}

const actions = {
  async load({ commit }) {
    try {
      const {
        data: { data },
      } = await SurveysServiceInstance.list()
      commit('setItems', data)
    } catch (e) {
      // Sin la lista no se rompe nada: se vuelve a pedir al reconectar.
    }
  },
  loadLater({ dispatch }) {
    return new Promise((resolve) => {
      setTimeout(
        () => dispatch('load').then(resolve, resolve),
        Math.random() * REFETCH_JITTER_MS,
      )
    })
  },
  // SurveysChanged: una o varias encuestas se activaron, cerraron, mostraron
  // u ocultaron (una acción masiva del admin llega en un solo aviso).
  // `roomId`: la sala donde está el asistente; si se activó la encuesta de
  // esa sala, se le abre sola.
  changed({ state, commit, dispatch, rootState }, { changes, roomId }) {
    let missing = false
    let toOpen = null
    // Las que se activaron para su sala pero no tenía cargadas.
    const missingToOpen = []

    changes.forEach((change) => {
      if (!change.is_visible) {
        commit('remove', change.uuid)
        return
      }
      const survey = state.items.find((row) => row.uuid === change.uuid)
      if (!survey) {
        // Se mostró una que no tiene (no trae las preguntas): pedir la lista.
        // Las encuestas nacen ocultas y la lista solo trae las visibles, así
        // que activar una recién creada caía siempre acá, y antes no se le
        // abría a nadie: solo aparecía en la lista. Ahora, si es de su sala,
        // se abre cuando llega la lista.
        missing = true
        if (change.status === 'open' && roomId && change.virtual_room_id === roomId) {
          missingToOpen.push(change.uuid)
        }
        return
      }
      commit('setStatus', { uuid: change.uuid, status: change.status })
      if (
        change.status === 'open' &&
        !survey.answered &&
        !toOpen &&
        roomId &&
        change.virtual_room_id === roomId
      ) {
        toOpen = survey
      }
    })

    if (state.active) {
      const current = state.items.find((row) => row.uuid === state.active.uuid)
      if (!current || current.status !== 'open') {
        commit('setActive', null)
      }
    }
    if (toOpen && !state.active) {
      commit('setActive', state.items.find((row) => row.uuid === toOpen.uuid))
    }
    if (missing) {
      dispatch('loadLater').then(() => {
        if (state.active || !missingToOpen.length) {
          return
        }
        // Sigue en la sala de la encuesta (la espera es de varios segundos).
        const room = rootState.event.show.data
        const survey = state.items.find(
          (row) =>
            missingToOpen.includes(row.uuid) &&
            row.status === 'open' &&
            !row.answered &&
            room &&
            row.virtual_room_id === room.id,
        )
        if (survey) {
          commit('setActive', survey)
        }
      })
    }
  },
  // Al terminar la charla responden miles a la vez: si el servidor está
  // saturado o la red falla, se reintenta solo antes de molestar al
  // asistente. Reintentar es seguro: el backend no acepta duplicados (409).
  async respond({ commit }, { uuid, answers }) {
    let attempt = 0
    for (;;) {
      try {
        await SurveysServiceInstance.respond(uuid, answers)
        break
      } catch (err) {
        const status = err.response && err.response.status
        // 409: ya la había respondido (otra pestaña, o un reintento cuyo
        // primer envío sí llegó). Para el asistente es lo mismo que enviarla.
        if (status === 409) {
          break
        }
        if (status === 404) {
          // El admin la cerró mientras respondía.
          commit('setStatus', { uuid, status: 'closed' })
          throw new SurveySubmitError('closed')
        }
        if (status === 422) {
          throw new SurveySubmitError('invalid', formErrorsHandler(err, true))
        }
        if (attempt >= RETRY_DELAYS_MS.length) {
          throw new SurveySubmitError('unavailable')
        }
        await sleep(RETRY_DELAYS_MS[attempt] + Math.random() * 1000)
        attempt++
      }
    }
    commit('setAnswered', uuid)
    commit('clearDraft', uuid)
    commit('setActive', null)
  },
  saveDraft({ commit }, { uuid, answers }) {
    commit('setDraft', { uuid, answers })
  },
  open({ commit }, survey) {
    commit('setActive', survey)
  },
  close({ commit }) {
    commit('setActive', null)
  },
  reset({ commit }) {
    commit('reset')
  },
}

const mutations = {
  setItems(state, items) {
    state.items = items
  },
  setStatus(state, { uuid, status }) {
    state.items = state.items.map((row) =>
      row.uuid === uuid ? { ...row, status } : row,
    )
  },
  remove(state, uuid) {
    state.items = state.items.filter((row) => row.uuid !== uuid)
  },
  setAnswered(state, uuid) {
    state.items = state.items.map((row) =>
      row.uuid === uuid ? { ...row, answered: true } : row,
    )
  },
  setActive(state, survey) {
    state.active = survey
  },
  setDraft(state, { uuid, answers }) {
    state.drafts = { ...state.drafts, [uuid]: answers }
  },
  clearDraft(state, uuid) {
    const { [uuid]: _, ...rest } = state.drafts
    state.drafts = rest
  },
  reset(state) {
    Object.assign(state, initState())
  },
}

export const Surveys = {
  namespaced: true,
  state: initState,
  getters,
  actions,
  mutations,
}
